import React from 'react';
import { cn } from '../cn';

interface ITypographyProps {
  theme?: 'dark' | 'light';
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'caption' | 'span' | 'p' | 'div' | 'header' | 'ul' | 'ol' | 'li' | 'label';
  weight?: 'light' | 'regular' | 'medium' | 'bold' | 'extrabold' | 'semibold' | 'inherit';
  color?: 'inherit' | 'text' | 'text-light' | 'text-lighter' | 'primary' | 'success' | 'warning' | 'error';
  align?: 'start' | 'center' | 'end';
  noWrap?: boolean;
  elipsis?: boolean;
  heading?: boolean;
  display?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  dangerouslySetInnerHTML?: { __html: string | TrustedHTML };
  expressive?: boolean;
}

const weightMap = {
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
  inherit: '',
};

const colorMap = {
  'inherit': '',
  'text': 'text-text',
  'text-light': 'text-text-light',
  'text-lighter': 'text-text-lighter',
  'primary': 'text-primary',
  'success': 'text-success',
  'warning': 'text-warning',
  'error': 'text-error',
};

const alignMap = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right',
};

const sizeBodyMapProductive = {
  xs: 'text-[14px] leading-5', //DUPE
  sm: 'text-[14px] leading-5',
  md: 'text-base leading-6',
  lg: 'text-[18px] leading-[26px]',
  xl: 'text-[18px] leading-[26px]', //DUPE
}

const sizeHeadingMapProductive = {
  xs: 'text-base leading-5 font-bold',
  sm: 'text-[18px] leading-[22px] font-bold',
  md: 'text-[20px] leading-[26px] font-extrabold lg:text-[22px]',
  lg: 'text-[22px] leading-[32px] font-extrabold md:text-[24px] lg:text-[26px]',
  xl: 'text-[26px] leading-[38px] font-extrabold md:text-[28px] lg:text-[32px]',
}

const sizeBodyMapExpressive = {
  xs: 'text-[14px] leading-[22px]', //DUPE
  sm: 'text-[14px] leading-[22px]',
  md: 'text-base leading-6 font-bold',
  lg: 'text-[18px] leading-7 font-bold',
  xl: 'text-[18px] leading-7 font-bold', //DUPE
}

const sizeHeadingMapExpressive = {
  xs: 'text-base leading-5 font-bold',
  sm: 'text-base leading-6 font-bold md:text-[18px]',
  md: 'text-[20px] leading-[32px] font-bold md:text-[22px] lg:text-[24px]',
  lg: 'text-[22px] leading-[36px] font-bold md:[24px] lg:text-[28px]',
  xl: 'text-[26px] leading-[46px] font-bold md:text-[30px] lg:text-[36px]',
}

const sizeDisplayMapExpressive = {
  xs: 'text-[18px] leading-[20px] font-semibold', //DUPE
  sm: 'text-[18px] leading-[20px] font-semibold',
  md: 'text-[26px] leading-[28px] font-bold md:text-[30px] md:leading-[32px] lg:text-[36px] lg:leading-[40px]',
  lg: 'text-[32px] leading-[36px] font-bold md:text-[36px] md:leading-[40px] lg:text-[48px] lg:leading-[52px]',
  xl: 'text-[32px] leading-[36px] font-bold md:text-[36px] md:leading-[40px] lg:text-[48px] lg:leading-[52px]', //DUPE
}

const wrapMap = {
  true: 'whitespace-no-wrap',
  false: '',
};

const elipsisMap = {
  true: 'overflow-hidden text-ellipsis max-w-full whitespace-nowrap',
  false: '',
};

export const Typography = React.forwardRef<any, React.PropsWithChildren<ITypographyProps>>((props, ref) => {
  const {
    theme = '',
    tag = 'span',
    weight = undefined,
    color = 'inherit',
    align = 'start',
    noWrap = false,
    elipsis = false,
    heading = false,
    display = false,
    expressive = false,
    size = 'md',
    children,
    className,
  } = props;

  const Tag = tag;
  const Weight = weight ? weightMap[weight] : undefined;
  const Color = colorMap[color];
  const Align = alignMap[align];
  const Wrap = wrapMap[noWrap.toString() as 'true' | 'false'];
  const Elipsis = elipsisMap[elipsis.toString() as 'true' | 'false'];
  const Heading = heading;
  const Display = display;
  const Expressive = expressive;
  const Size = Expressive ? (
    Display ? sizeDisplayMapExpressive[size] : Heading ? sizeHeadingMapExpressive[size] : sizeBodyMapExpressive[size]
  ) : (
    Display ? sizeHeadingMapProductive[size] : Heading ? sizeHeadingMapProductive[size] : sizeBodyMapProductive[size]
  )

  return (
    <Tag
      className={cn(
        'block',
        theme === 'dark' ? 'dark' : theme === 'light' ? 'light' : '',
        Size,
        Weight,
        Align,
        Wrap,
        Elipsis,
        'font-text',
        Expressive && 'font-expressive-text',
        Heading && 'font-heading',
        Display && 'font-display',
        Heading && Expressive && 'font-expressive-heading',
        Display && Expressive && 'font-expressive-display',
        Color,
        className,
      )}
      dangerouslySetInnerHTML={props.dangerouslySetInnerHTML}
      ref={ref}
    >
      {children}
    </Tag>
  );
});
